import { useCallback, useEffect, useState } from 'react'
import remindersApi from '../client/reminders-api'
import { useAuth } from '../context/AuthContext'
import { useSnackbar } from '../context/snackbar-context'

// New-episode reminder state for one show (the Remind Me bell on the show
// page). Loads the user's reminder list on mount and exposes an optimistic
// toggle. Independent from the watchlist.
export const useShowReminder = (tmdbShowId: number) => {
  const { isAuthenticated, isAuthReady } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [isReminding, setIsReminding] = useState(false)
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    // Wait for a token (isAuthReady), not just an Auth0 session, so this
    // on-mount fetch never fires bare on a fresh tab.
    if (!isAuthReady || !tmdbShowId) return
    let cancelled = false
    remindersApi
      .getMine()
      .then((response) => {
        if (cancelled || !Array.isArray(response.data)) return
        setIsReminding(response.data.some((reminder) => reminder.tmdbShowId === tmdbShowId))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isAuthReady, tmdbShowId])

  const toggleReminder = useCallback(
    async (meta: { title: string; posterPath?: string | null }) => {
      if (!isAuthenticated || isPending) return
      setIsPending(true)
      const next = !isReminding
      setIsReminding(next)
      try {
        if (next) {
          await remindersApi.add({ tmdbShowId, title: meta.title, posterPath: meta.posterPath })
          enqueueSnackbar('New episode reminders on', { variant: 'success' })
        } else {
          await remindersApi.remove(tmdbShowId)
          enqueueSnackbar('New episode reminders off', { variant: 'success' })
        }
      } catch {
        setIsReminding(!next)
        enqueueSnackbar('Failed to update reminder', { variant: 'error' })
      } finally {
        setIsPending(false)
      }
    },
    [isAuthenticated, isPending, isReminding, tmdbShowId, enqueueSnackbar]
  )

  return { isReminding, isReminderPending: isPending, toggleReminder }
}
